import React, { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { FileText, Users, Package, Settings, Menu, X, LogOut, HelpCircle } from 'lucide-react';
import { useState } from 'react';
import { destroySession, updateActivity, isSessionValid } from '@/lib/auth';

const NAV = [
  { to: '/',          label: 'Presupuestos', icon: FileText,    key: 'presupuestos' },
  { to: '/clientes',  label: 'Clientes',     icon: Users,       key: 'clientes' },
  { to: '/productos', label: 'Productos',    icon: Package,     key: 'productos' },
  { to: '/configuracion', label: 'Config.', icon: Settings,    key: 'config' },
  { to: '/manual',    label: 'Ayuda',        icon: HelpCircle,  key: 'manual' },
];

interface Props {
  title: string;
  active: string;
  children: React.ReactNode;
}

export function AppLayout({ title, active, children }: Props) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  // ── Detector de inactividad ──────────────────────────────────────────────
  const handleActivity = useCallback(() => { updateActivity(); }, []);

  useEffect(() => {
    // Registra actividad en cualquier interacción del usuario
    const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];
    events.forEach(ev => window.addEventListener(ev, handleActivity, { passive: true }));

    // Verifica sesión cada 60 segundos (caducidad + inactividad)
    const ticker = setInterval(() => {
      if (!isSessionValid()) {
        destroySession();
        window.location.reload();
      }
    }, 60_000);

    return () => {
      events.forEach(ev => window.removeEventListener(ev, handleActivity));
      clearInterval(ticker);
    };
  }, [handleActivity]);

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* ── Top bar ── */}
      <header className="border-b border-border bg-card sticky top-0 z-40"
        style={{ boxShadow: '0 1px 8px -2px color-mix(in srgb, var(--foreground) 5%, transparent)' }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between gap-4">
          {/* Logo / nombre */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 font-bold text-sm tracking-tight text-foreground hover:text-primary transition-colors"
          >
            <FileText size={18} className="text-primary" />
            <span>
              Presupuesto Pro
              <span className="hidden sm:inline text-xs font-normal text-muted-foreground ml-1.5">
                by{' '}
                <a
                  href="https://movilizatic.net"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors"
                  onClick={e => e.stopPropagation()}
                >
                  movilizatic.net
                </a>
              </span>
            </span>
          </button>

          {/* Nav desktop */}
          <nav className="hidden sm:flex items-center gap-1">
            {NAV.map(({ to, label, icon: Icon, key }) => (
              <Button
                key={key}
                variant={active === key ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => navigate(to)}
                className={`gap-1.5 text-sm ${active === key ? 'font-semibold' : ''}`}
              >
                <Icon size={15} /> {label}
              </Button>
            ))}
          </nav>

          {/* Cerrar sesión + Hamburger mobile */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost" size="icon"
              className="hidden sm:flex h-9 w-9 text-muted-foreground hover:text-destructive"
              title="Cerrar sesión"
              onClick={() => { destroySession(); window.location.reload(); }}
            >
              <LogOut size={16} />
            </Button>
            <Button
              variant="ghost" size="icon"
              className="sm:hidden h-9 w-9"
              onClick={() => setMenuOpen(o => !o)}
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="sm:hidden border-t border-border bg-card px-4 py-3 flex flex-col gap-1">
            {NAV.map(({ to, label, icon: Icon, key }) => (
              <Button
                key={key}
                variant={active === key ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => { navigate(to); setMenuOpen(false); }}
                className="justify-start gap-2"
              >
                <Icon size={15} /> {label}
              </Button>
            ))}
          </div>
        )}
      </header>

      {/* ── Page content ── */}
      <main className="flex-1">
        {children}
      </main>
    </div>
  );
}
